import React, { useCallback, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Captions } from '~components/Typography';
import annalsUserSettings from '~features/annals/hooks/userSettings';
import type { Tab, UserInfoForm } from '~features/userInfo/model/types';
import {
	BottomActions,
	StyledActions,
} from '~features/userInfo/ui/Actions/styles';
import { DeleteAccountModal } from '~features/userInfo/ui/DeleteAccountModal';
import { useDeleteAccount } from '~hooks/useDeleteAccount';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { ButtonPrimary } from '~shared/ui/atoms/Buttons/ButtonPrimary';
import { ButtonSecondary } from '~shared/ui/atoms/Buttons/ButtonSecondary';
import type { AppDispatch } from '~store/index';
import { logout } from '~store/utils/logout';

const useBackClickAnnals = () => {
	const sendAnnalsProfileBack = annalsUserSettings.useProfileBackClicked();
	const sendAnnalsContentBack = annalsUserSettings.useContentBackClicked();
	const sendAnnalsSecurityBack = annalsUserSettings.useSecurityBackClicked();

	const annals = useMemo(
		() => ({
			profile: sendAnnalsProfileBack,
			content: sendAnnalsContentBack,
			security: sendAnnalsSecurityBack,
		}),
		[sendAnnalsContentBack, sendAnnalsProfileBack, sendAnnalsSecurityBack]
	);

	return annals;
};

export const Actions = ({
	form,
	tab,
	onClose,
	isUserId,
}: {
	form: UseFormReturn<UserInfoForm, unknown, UserInfoForm>;
	isUserId?: boolean;
	tab: Tab;
	onClose: () => void;
}) => {
	const { userId } = useSession();
	const t = useTranslations('SettingsMenu');

	const dispatch = useDispatch<AppDispatch>();

	const triggerLogout = useCallback(() => {
		dispatch(logout());
	}, [dispatch]);

	const deleteAccount = useDeleteAccount();

	const cancelDeleteAccount = useCallback(() => {
		void deleteAccount.cancel();
	}, [deleteAccount]);

	const tabBackAnnals = useBackClickAnnals();

	const handleGoBack = useCallback(() => {
		tabBackAnnals[tab]();
		onClose();
	}, [tab, tabBackAnnals, onClose]);

	return (
		<>
			<StyledActions>
				<ButtonPrimary
					loading={form.formState.isSubmitting}
					disabled={!form.formState.isDirty}
					size="L"
					type="submit"
				>
					{t('saveChangesButtonTitle')}
				</ButtonPrimary>
				{form?.formState?.errors?.root?.message && (
					<Captions size="L" color="warn">
						{form?.formState?.errors?.root?.message}
					</Captions>
				)}
				<ButtonSecondary
					type="button"
					onClick={handleGoBack}
					disabled={form.formState.isSubmitting}
					size="L"
				>
					{t('goBackButtonTitle')}
				</ButtonSecondary>
				{isUserId ? (
					<Captions color="semi-transparent" size="L">
						id {userId}
					</Captions>
				) : (
					<BottomActions>
						<Captions onClick={triggerLogout} color="semi-transparent" size="L">
							{t('logOutButtonTitle')}
						</Captions>
						<Captions color="white-s" size="L">
							●
						</Captions>
						<Captions
							color="semi-transparent"
							onClick={deleteAccount.confirm}
							size="L"
						>
							{t('deleteAccountButtonTitle')}
						</Captions>
					</BottomActions>
				)}
			</StyledActions>

			<DeleteAccountModal
				locked={false}
				open={deleteAccount.confirmOpened}
				onClose={cancelDeleteAccount}
				onCancel={cancelDeleteAccount}
				onConfirm={deleteAccount.act}
			/>
		</>
	);
};
